import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, inject, onMounted } from "vue";
import { useDeviceStore } from "@/stores/deviceStore";
import { useSurveyStore } from "@/stores/surveyStore";
import NavigationBar from "@/components/shared/NavigationBar.vue";
import FooterButton from "@/components/shared/button/FooterButton.vue";
import BaseTextarea from "@/components/shared/inputs/base/BaseTextarea.vue";
import {
  LineTypeCode,
  LineTypeName,
  PROVIDE_KEY_SHOW_ERROR_MODAL,
  PROVIDE_KEY_SHOW_TOAST_MESSAGE,
  ShowErrorModal,
  ShowToastMessage,
} from "@/types";
import Page from "@/components/shared/Page.vue";
import { ISurvey } from "@/domain/entities/survey";
import { useRoute } from "vue-router";
import router from "@/router";
import InlineSvg from "vue-inline-svg";
import LineTypeChip from "@/components/shared/chip/LineTypeChip.vue";
import { useLookupLineTypeCode } from "@/use/useLookupLineTypeCode";
import useGetLineTypeName from "@/use/useGetLineTypeName";
import ScrollTopControl from "@/components/shared/ScrollTopControl.vue";


const __default__ = {
  name: "BoardEvaluation",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props, { expose: __expose }) {
  __expose();

const route = useRoute();
const deviceStore = useDeviceStore();
const surveyStore = useSurveyStore();
const {
  lineTurnUid,
  allocUid,
  lineTypeCode: lineTypeCodeQuery,
  lineName,
  campusName,
} = route.query as {
  lineTurnUid: string;
  allocUid: string;
  lineTypeCode: string;
  lineTypeName: string;
  lineName: string;
  campusName: string;
};

const contentWrapper = ref<HTMLElement>();

const lineTypeCode = useLookupLineTypeCode(lineTypeCodeQuery) as LineTypeCode;
const lineTypeName = useGetLineTypeName(lineTypeCode) as LineTypeName;

const showErrorModal = inject(PROVIDE_KEY_SHOW_ERROR_MODAL) as ShowErrorModal;
const showToastMessage = inject(
  PROVIDE_KEY_SHOW_TOAST_MESSAGE
) as ShowToastMessage;

const selectedRating = ref<number>(0);
const feedbackText = ref<string>(""); // 별점이 1인 경우에만 보여준다
const userAnswers = ref<Map<number, Map<string, string | string[]>>>(new Map());

// 별점 클릭 시 호출되는 함수
const handleRatingSelect = (rating: number) => {
  selectedRating.value = rating;

  // 별점이 1이 아닌 경우 텍스트 입력을 초기화하고 inputValue 제거
  if (rating !== 1) {
    feedbackText.value = "";
  }

  // userAnswers 업데이트 (질문 번호 1에 대한 답변)
  const answerMap = new Map<string, string | string[]>();
  answerMap.set("selected", rating.toString()); // 선택된 별점

  userAnswers.value.set(1, answerMap); // 질문 번호 1에 대한 답변 저장
};

const ratingDescription = computed(() => {
  return selectedRating.value
    ? ["별로에요", "그저그래요", "보통이에요", "좋아요", "최고에요"][
        selectedRating.value - 1
      ]
    : "";
});

const onInputTextarea = (opinion: string) => {
  if (selectedRating.value !== 1) return;

  const answerMap = userAnswers.value.get(1) || new Map<string, string>();
  feedbackText.value = opinion;

  const feedback = feedbackText.value.trim() ? `(${feedbackText.value})` : "";
  answerMap.set("selected", `${selectedRating.value}${feedback}`);
  userAnswers.value.set(1, answerMap);
};

const isEvaluable = computed(
  () =>
    lineSurvey.value &&
    selectedRating.value !== 0 &&
    (selectedRating.value === 1 ? !!feedbackText.value.length : true)
);

const onClickEvaluate = async () => {
  if (!lineSurvey.value) return;

  userAnswers.value = new Map([...userAnswers.value.entries()].sort());
  const sendSurveyResult = await surveyStore.sendSurveyResult(
    lineSurvey.value.id,
    userAnswers.value,
    lineTurnUid,
    allocUid
  );

  if (!sendSurveyResult.isSuccess) {
    showErrorModal(
      "서비스평가 제출 오류",
      sendSurveyResult.errorMessage,
      sendSurveyResult.resultCode,
      sendSurveyResult.errorTime
    );
    return;
  }

  router.back();
  showToastMessage("서비스 평가 완료되었습니다.", 3000, 350);
};

const textArea = ref();
const serviceEvaluation = ref();
const setTextAreaScroll = () => {
  const textAreaY = textArea.value?.getBoundingClientRect().top || 0;
  const serviceEvaluationY =
    serviceEvaluation.value.getBoundingClientRect().top;
  const offset = textAreaY - serviceEvaluationY;
  serviceEvaluation.value.scrollTo({
    top:
      offset > 0
        ? offset + serviceEvaluation.value.scrollTop
        : serviceEvaluation.value.scrolltop,
    behavior: "smooth",
  });
};

const goToBack = () => {
  route.meta.willGoBack = true;
  router.back();
};

const lineSurvey = ref<ISurvey>();
const getServiceEvaluation = async () => {
  const lineSurveyResult = await surveyStore.getServiceEvaluationContents();
  if (!lineSurveyResult.data) {
    showErrorModal(
      "서비스 평가 불가",
      "서비스 평가 기간이 아닙니다.",
      undefined,
      undefined,
      goToBack
    );
    return;
  }

  lineSurvey.value = lineSurveyResult.data;
};

const isShowingKeyboard = computed(() => deviceStore.isShowingKeyboard);
watch(isShowingKeyboard, (isShowingKeyboard) => {
  if (isShowingKeyboard) {
    setTextAreaScroll();
    return;
  }
});

onMounted(() => {
  getServiceEvaluation();
});

const __returned__ = { route, deviceStore, surveyStore, lineTurnUid, allocUid, lineTypeCodeQuery, lineName, campusName, contentWrapper, lineTypeCode, lineTypeName, showErrorModal, showToastMessage, selectedRating, feedbackText, userAnswers, handleRatingSelect, ratingDescription, onInputTextarea, isEvaluable, onClickEvaluate, textArea, serviceEvaluation, setTextAreaScroll, goToBack, lineSurvey, getServiceEvaluation, isShowingKeyboard, NavigationBar, FooterButton, BaseTextarea, Page, get InlineSvg() { return InlineSvg }, LineTypeChip, ScrollTopControl }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})